import type { DataQualityRow, LoyaltyRow, MasterProfile, OrderRow } from "../../../types";
import { routePaths } from "../../../routes/navigation";
import { buildPath, getProfileNextAction } from "./shared";
import type { ClientActionInboxItem } from "./types";

export function buildClientActionInboxItems(
    profiles: MasterProfile[],
    loyaltyRows: LoyaltyRow[],
    orderRows: OrderRow[],
    issues: DataQualityRow[]
): ClientActionInboxItem[] {
    const items: ClientActionInboxItem[] = [];
    const loyaltyByCardNumber = new Map(loyaltyRows.map((row) => [row.cardNumber, row] as const));
    const ordersByCustomer = new Map<string, OrderRow[]>();
    const issuesByEntity = new Map<string, DataQualityRow[]>();

    for (const order of orderRows) {
        const existingOrders = ordersByCustomer.get(order.customer);

        if (existingOrders) {
            existingOrders.push(order);
        } else {
            ordersByCustomer.set(order.customer, [order]);
        }
    }

    for (const issue of issues) {
        const existingIssues = issuesByEntity.get(issue.entity);

        if (existingIssues) {
            existingIssues.push(issue);
        } else {
            issuesByEntity.set(issue.entity, [issue]);
        }
    }

    function pushInboxItem(item: ClientActionInboxItem) {
        const existing = items.find((entry) => entry.id === item.id);
        if (!existing) {
            items.push(item);
            return;
        }

        if (item.score > existing.score) {
            existing.score = item.score;
            existing.summary = item.summary;
            existing.priority = item.priority;
            existing.ctaLabel = item.ctaLabel;
            existing.ctaPath = item.ctaPath;
            existing.title = item.title;
            existing.evidence = item.evidence;
        } else {
            existing.evidence = Array.from(new Set([...existing.evidence, ...item.evidence]));
        }
    }

    for (const profile of profiles) {
        const profileOrders = ordersByCustomer.get(profile.customer) ?? [];
        const relatedIssues = [
            ...(issuesByEntity.get(profile.profileId) ?? []),
            ...(issuesByEntity.get(profile.loyaltyCard) ?? []),
            ...profileOrders.flatMap((order) => issuesByEntity.get(order.orderNumber) ?? []),
        ];
        const loyaltyCard = loyaltyByCardNumber.get(profile.loyaltyCard) ?? null;
        const nextAction = getProfileNextAction(profile, relatedIssues.length);
        const highSeverityIssues = relatedIssues.filter((issue) => issue.severity === "Високо");
        const mediumSeverityIssues = relatedIssues.filter((issue) => issue.severity === "Средно");

        if (profile.status === "Рисков клиент" || highSeverityIssues.length > 0) {
            pushInboxItem({
                id: `${profile.profileId}-risk`,
                profileId: profile.profileId,
                title: "Провери рисков клиентски профил",
                summary: `${profile.customer} е с висок риск и има ${highSeverityIssues.length || 1} критични сигнала, които блокират следващи действия.`,
                priority: "Висок",
                focus: "Качество",
                ctaLabel: "Към ",
                ctaPath: buildPath(routePaths.client360, {
                    profile: profile.profileId,
                }),
                owner: "Екип данни",
                evidence: [
                    `Статус: ${profile.status}`,
                    `${highSeverityIssues.length} висок приоритетен сигнал`,
                ],
                score: 95,
            });
        }

        if (profile.consent === "Липсва") {
            pushInboxItem({
                id: `${profile.profileId}-consent`,
                profileId: profile.profileId,
                title: "Събери липсващо съгласие",
                summary: `${profile.customer} още няма валидно съгласие и не е готов за кампанийни аудитории.`,
                priority: "Висок",
                focus: "Идентичност",
                ctaLabel: "Към Клиентско досие",
                ctaPath: buildPath(routePaths.client360, {
                    profile: profile.profileId,
                }),
                owner: "CRM екип",
                evidence: [
                    "Липсва съгласие",
                    `Канали: ${profile.channels}`,
                ],
                score: 88,
            });
        }

        if (!loyaltyCard || loyaltyCard.status === "Нужна валидация") {
            pushInboxItem({
                id: `${profile.profileId}-loyalty`,
                profileId: profile.profileId,
                title: loyaltyCard ? "Валидирай карта за лоялност" : "Предложи регистрация в лоялната програма",
                summary: loyaltyCard
                    ? `${loyaltyCard.cardNumber} изисква валидация, за да може профилът да стане реално омниканален.`
                    : `${profile.customer} още няма свързана карта и губим част от разпознаването между каналите.`,
                priority: loyaltyCard ? "Среден" : "Висок",
                focus: "Лоялност",
                ctaLabel: "Към Лоялност",
                ctaPath: buildPath(routePaths.loyalty, {
                    search: loyaltyCard?.cardNumber ?? profile.customer,
                    status: loyaltyCard?.status,
                    card: loyaltyCard?.cardNumber,
                }),
                owner: "Екип лоялност",
                evidence: [
                    loyaltyCard ? `Статус на картата: ${loyaltyCard.status}` : "Липсва карта",
                    `Омниканално използване: ${loyaltyCard?.omnichannelUse ?? "Няма"}`,
                ],
                score: loyaltyCard ? 68 : 82,
            });
        }

        if (profileOrders.length === 0) {
            pushInboxItem({
                id: `${profile.profileId}-orders`,
                profileId: profile.profileId,
                title: "Потвърди връзката с поръчки",
                summary: `${profile.customer} няма видими свързани поръчки и 360 историята остава непълна.`,
                priority: "Среден",
                focus: "Качество",
                ctaLabel: "Към Поръчки",
                ctaPath: buildPath(routePaths.orders, {
                    search: profile.customer,
                }),
                owner: "Операции",
                evidence: [
                    "Няма видими поръчки",
                    `Предпочитан магазин: ${profile.preferredStore}`,
                ],
                score: 64,
            });
        }

        if (profile.status === "Частичен" && profileOrders.length > 0 && loyaltyCard) {
            pushInboxItem({
                id: `${profile.profileId}-identity`,
                profileId: profile.profileId,
                title: "Затвори частичната идентичност",
                summary: `${profile.customer} има активност в няколко слоя, но профилът още е частичен и иска финализиране.`,
                priority: "Среден",
                focus: "Идентичност",
                ctaLabel: "Към Клиентско досие",
                ctaPath: buildPath(routePaths.client360, {
                    profile: profile.profileId,
                }),
                owner: "CRM екип",
                evidence: [
                    "Статус: Частичен",
                    `${profileOrders.length} поръчки`,
                    `Карта: ${loyaltyCard.cardNumber}`,
                ],
                score: 74,
            });
        }

        if (mediumSeverityIssues.length >= 2) {
            pushInboxItem({
                id: `${profile.profileId}-quality`,
                profileId: profile.profileId,
                title: "Натрупват се сигнали за качество",
                summary: `${profile.customer} има ${mediumSeverityIssues.length} средни сигнала и рискът вече е оперативно значим.`,
                priority: "Среден",
                focus: "Качество",
                ctaLabel: "Към Клиентско досие",
                ctaPath: buildPath(routePaths.client360, {
                    profile: profile.profileId,
                }),
                owner: "Екип данни",
                evidence: [
                    `${mediumSeverityIssues.length} сигнала със средна тежест`,
                    `Следващо действие: ${nextAction}`,
                ],
                score: 72,
            });
        }

        if (items.every((item) => !item.id.startsWith(profile.profileId))) {
            pushInboxItem({
                id: `${profile.profileId}-next`,
                profileId: profile.profileId,
                title: "Профилът е готов за следваща стъпка",
                summary: nextAction,
                priority: "Среден",
                focus: "Идентичност",
                ctaLabel: "Към Клиентско досие",
                ctaPath: buildPath(routePaths.client360, {
                    profile: profile.profileId,
                }),
                owner: "CRM екип",
                evidence: [
                    `Съгласие: ${profile.consent}`,
                    `Канали: ${profile.channels}`,
                ],
                score: 48,
            });
        }
    }

    const priorityRank = {
        Висок: 0,
        Среден: 1,
    } as const;

    return items
        .sort((left, right) => {
            const scoreDelta = right.score - left.score;
            if (scoreDelta !== 0) return scoreDelta;

            const priorityDelta = priorityRank[left.priority] - priorityRank[right.priority];
            if (priorityDelta !== 0) return priorityDelta;

            return left.profileId.localeCompare(right.profileId);
        })
        .slice(0, 8);
}
